import { useEffect, useRef, useState } from 'react';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Button, Icon, Toggle } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { advancedLocalStorage } from '@sendible/common';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { useInView } from 'react-intersection-observer';
import {
  Container,
  Content,
  Display,
  Divider,
  ExportButton,
  Item,
  ItemLabel,
  List,
  ListDisplay,
  ListItem,
  Stepper,
  StepperValue,
  VisuallyHidden,
} from './index.styles';
import { POSTS_PER_DAY_LIMITS, useCalendarContext } from '../../context';
import { ExportModal } from '../ExportModal';

interface Option {
  action: string;
  label: string;
}

const STORAGE_KEY = 'showHolidays';

export const OptionsDropdown = () => {
  const user = useBridgeUser();
  const {
    density,
    setDensity,
    setShowHolidays,
    showHolidays,
    viewMode,
    dailySortOrder,
    setDailySortOrder,
    hideEmptyDates,
    setHideEmptyDates,
    postsPerDay,
    setPostsPerDay,
  } = useCalendarContext();
  const { min: minPostsPerDay, max: maxPostsPerDay } = POSTS_PER_DAY_LIMITS[density];
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openList, setOpenList] = useState<null | 'mode' | 'density' | 'sortOrder'>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { t } = useTranslation('calendar');

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const { ref: densityInViewRef } = useInView({
    threshold: 1,
    rootMargin: '8px',
    onChange: (nextInView) => {
      if (!nextInView) {
        setIsCollapsed(false);
      }
    },
  });

  const {
    x,
    y,
    strategy,
    refs: { setReference, setFloating },
  } = useFloating({
    open: isCollapsed,
    onOpenChange: setIsCollapsed,
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const setFloatingAndDropdownRef = (node: HTMLDivElement | null) => {
    setFloating(node);
    dropdownRef.current = node;
  };

  const handleLists = (option: 'mode' | 'density' | 'sortOrder') => {
    setOpenList((prev) => (prev === option ? null : option));
  };

  const handleDensitySelect = (type: CalendarDensity) => {
    setDensity(type);
    setOpenList(null);
  };

  // The order only changes how the loaded days are listed, not what is loaded, so nothing is fetched again and the
  // list stays on the date that is in view.
  const handleSortOrderSelect = (order: 'oldest' | 'newest') => {
    setDailySortOrder(order);
    setOpenList(null);
  };

  const handlePostsPerDayChange = (delta: 1 | -1) => {
    const next = postsPerDay + delta;

    if (next < minPostsPerDay || next > maxPostsPerDay) return;

    setPostsPerDay(next);
  };

  const handleExportClick = () => {
    setIsCollapsed(false);
    setOpenList(null);
    setIsExportModalOpen(true);
  };

  const hierarchicalClose = () => {
    if (openList !== null) {
      setOpenList(null);

      return;
    }
    if (isCollapsed) {
      setIsCollapsed(false);
    }
  };

  const densities: Option[] = [
    { action: 'comfortable', label: t('comfortable') },
    { action: 'compact', label: t('compact') },
  ];

  const sortOrders: Option[] = [
    { action: 'oldest', label: t('oldest') },
    { action: 'newest', label: t('newest') },
  ];

  const handleHoliday = (value: boolean) => {
    setShowHolidays(value);
    advancedLocalStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    window.pendo.track('[Publish] Calendar - Holidays toggled', { status: value ? 'on' : 'off' });
  };

  const handleHideEmptyDates = (value: boolean) => {
    setHideEmptyDates(value);
    window.pendo.track('[Publish] Calendar - Hide empty dates toggled', { status: value ? 'on' : 'off' });
  };

  useEffect(() => {
    if (typeof user?.userId === 'undefined') return;

    const stored = advancedLocalStorage.getItem(STORAGE_KEY);

    if (stored === 'true') {
      setShowHolidays(true);

      return;
    }
    if (stored === 'false') setShowHolidays(false);
  }, [user?.userId, setShowHolidays]);

  useEffect(() => {
    if (!isCollapsed) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const path = (event.composedPath && event.composedPath()) || [];
      const inContainer = containerRef.current ? containerRef.current.contains(target) || path.includes(containerRef.current) : false;
      const inDropdown = dropdownRef.current ? dropdownRef.current.contains(target) || path.includes(dropdownRef.current) : false;

      if (!inContainer && !inDropdown) {
        hierarchicalClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);

    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [isCollapsed, openList]);

  useEffect(() => {
    if (openList === null) return;

    const handleAnyClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (target?.closest('[data-list-interactive="true"]')) {
        return;
      }

      setOpenList(null);
    };

    document.addEventListener('mousedown', handleAnyClick, true);

    return () => document.removeEventListener('mousedown', handleAnyClick, true);
  }, [openList]);

  useEffect(() => {
    if (!isCollapsed) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hierarchicalClose();
    };

    document.addEventListener('keydown', handleKey);

    return () => document.removeEventListener('keydown', handleKey);
  }, [isCollapsed, openList]);

  return (
    <Container
      data-testid="calendar-options-dropdown"
      ref={containerRef}
      data-export-hide
    >
      <Display
        data-testid="calendar-options-toggle"
        onClick={() => setIsCollapsed((prev) => !prev)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setIsCollapsed((prev) => !prev)}
        ref={setReference}
        tabIndex={0}
        $isCollapsed={isCollapsed}
      >
        <Icon
          color="brandDark"
          name="sliders"
          strokeWidth={2}
        />
      </Display>

      {isCollapsed &&
        createPortal(
          <Content
            data-testid="options-content-modal"
            ref={setFloatingAndDropdownRef}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            {viewMode !== 'daily' && (
              <Item data-testid="calendar-holiday-selector">
                <ItemLabel data-testid="calendar-holiday-label">{t('holidays')}</ItemLabel>
                <Toggle
                  checked={showHolidays}
                  onChange={(e) => handleHoliday(e.target.checked)}
                  size="sm"
                />
              </Item>
            )}
            <Item>
              <ItemLabel>{t('density')}</ItemLabel>
              <ListDisplay
                $isCollapsed={openList === 'density'}
                onClick={() => handleLists('density')}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleLists('density')}
                tabIndex={0}
                data-list-interactive="true"
                ref={densityInViewRef}
              >
                <span>{t(density)}</span>
                <Icon
                  customClass="display-chevron"
                  color="brandDark"
                  name="chevron_down"
                  strokeWidth={2}
                />
              </ListDisplay>
              {openList === 'density' && (
                <List data-list-interactive="true">
                  {densities.map((option) => (
                    <ListItem
                      key={option.action}
                      onClick={() => handleDensitySelect(option.action as CalendarDensity)}
                    >
                      <span>{option.label}</span>
                    </ListItem>
                  ))}
                </List>
              )}
            </Item>
            {viewMode === 'monthly' && (
              <Item data-testid="calendar-posts-per-day">
                <ItemLabel>{t('posts_per_day')}</ItemLabel>
                <Stepper>
                  <Button
                    data-testid="calendar-posts-per-day-decrease"
                    aria-label={t('show_fewer_posts_per_day')}
                    aria-disabled={postsPerDay <= minPostsPerDay}
                    icon="minus"
                    onClick={() => handlePostsPerDayChange(-1)}
                    variation="outline"
                  />
                  <StepperValue data-testid="calendar-posts-per-day-value">{postsPerDay}</StepperValue>
                  <Button
                    data-testid="calendar-posts-per-day-increase"
                    aria-label={t('show_more_posts_per_day')}
                    aria-disabled={postsPerDay >= maxPostsPerDay}
                    icon="plus"
                    onClick={() => handlePostsPerDayChange(1)}
                    variation="outline"
                  />
                </Stepper>
                <VisuallyHidden
                  role="status"
                  aria-live="polite"
                  data-testid="calendar-posts-per-day-status"
                >
                  {t('posts_per_day_value', { count: postsPerDay })}
                </VisuallyHidden>
              </Item>
            )}
            {viewMode === 'daily' && (
              <Item data-testid="calendar-hide-empty-dates-selector">
                <ItemLabel data-testid="calendar-hide-empty-dates-label">{t('hide_empty_dates')}</ItemLabel>
                <Toggle
                  checked={hideEmptyDates}
                  onChange={(e) => handleHideEmptyDates(e.target.checked)}
                  size="sm"
                  testId="calendar-hide-empty-dates-toggle"
                />
              </Item>
            )}
            {viewMode === 'daily' && (
              <Item>
                <ItemLabel>{t('sort_by')}</ItemLabel>
                <ListDisplay
                  $isCollapsed={openList === 'sortOrder'}
                  onClick={() => handleLists('sortOrder')}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleLists('sortOrder')}
                  tabIndex={0}
                  data-list-interactive="true"
                  data-testid="calendar-sort-order-display"
                >
                  <span>{t(dailySortOrder)}</span>
                  <Icon
                    customClass="display-chevron"
                    color="brandDark"
                    name="chevron_down"
                    strokeWidth={2}
                  />
                </ListDisplay>
                {openList === 'sortOrder' && (
                  <List data-list-interactive="true">
                    {sortOrders.map((option) => (
                      <ListItem
                        key={option.action}
                        onClick={() => handleSortOrderSelect(option.action as 'oldest' | 'newest')}
                        data-testid={`calendar-sort-order-option-${option.action}`}
                      >
                        <span>{option.label}</span>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Item>
            )}
            <Divider />
            <ExportButton
              data-testid="calendar-view-export-options"
              onClick={handleExportClick}
            >
              {t('export_ellipsed')}
            </ExportButton>
          </Content>,
          document.getElementById('dropdown-root') as HTMLElement
        )}

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
    </Container>
  );
};
